import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Search, Package, ShoppingCart } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { authFetch } from '@/services/api.service';
import type { PurchaseOrderFormData } from '@/types/purchase-order.types';
import type { Supplier } from '@/types/suppliers.types';
import type { Ingredient } from '@/types/ingredient.types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

interface PurchaseOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (orderData: PurchaseOrderFormData) => Promise<boolean>;
  suppliers: Supplier[];
  loading?: boolean;
}

interface OrderItem {
  ingredientId: string;
  ingredient: Ingredient;
  quantity: number;
  unitCost: number;
  totalCost: number;
}

// Interface for database ingredient from API
interface DatabaseIngredient {
  id: string;
  name: string;
  description?: string;
  costUnit: number;
  supplierId?: string;
  unit: string;
  category: string;
  supplierName?: string;
  stockActual: number;
  consumo: number;
}

export const PurchaseOrderModal: React.FC<PurchaseOrderModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  suppliers,
  loading = false
}) => {
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [ingredientsLoading, setIngredientsLoading] = useState(false);
  const [formData, setFormData] = useState<{
    supplierName: string;
    supplierId?: string;
    locationId?: string;
    expectedDeliveryDate?: string;
    notes?: string;
  }>({
    supplierName: '',
    supplierId: '',
    locationId: 'loc-1',
    expectedDeliveryDate: '',
    notes: ''
  });
  
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showIngredients, setShowIngredients] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Load ingredients from database when modal opens
  useEffect(() => {
    if (!isOpen) return;

    const loadIngredients = async () => {
      try {
        setIngredientsLoading(true);
        const response = await authFetch(`${API_BASE_URL}/purchase-orders/ingredients`);

        if (!response.ok) throw new Error('Error al cargar ingredientes');

        const data = await response.json();
        if (data.success) {
          // Transform database ingredients to expected format
          const transformedIngredients: Ingredient[] = data.data.map((dbIngredient: DatabaseIngredient) => ({
            id: dbIngredient.id,
            name: dbIngredient.name,
            description: dbIngredient.description || '',
            category: dbIngredient.category,
            unit: dbIngredient.unit,
            supplier: dbIngredient.supplierName || 'Sin proveedor',
            costPerUnit: dbIngredient.costUnit,
            globalCostPerUnit: dbIngredient.costUnit,
            isActive: true,
            locationStocks: [{
              locationId: 'loc-1',
              currentStock: dbIngredient.stockActual,
              minimumStock: 0,
              maximumStock: 1000
            }]
          }));
          setIngredients(transformedIngredients);
        }
      } catch (error) {
        console.error('Error loading ingredients:', error);
        toast.error('Error al cargar ingredientes');
      } finally {
        setIngredientsLoading(false);
      }
    };

    loadIngredients();
  }, [isOpen]);

  const filteredIngredients = ingredients.filter(ingredient =>
    ingredient.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    ingredient.category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const addIngredientToOrder = (ingredient: Ingredient) => {
    const existingItem = orderItems.find(item => item.ingredientId === ingredient.id);
    
    if (existingItem) {
      setOrderItems(prev => prev.map(item =>
        item.ingredientId === ingredient.id
          ? { ...item, quantity: item.quantity + 1, totalCost: (item.quantity + 1) * item.unitCost }
          : item
      ));
    } else {
      const newItem: OrderItem = {
        ingredientId: ingredient.id,
        ingredient,
        quantity: 0,
        unitCost: ingredient.costPerUnit || ingredient.globalCostPerUnit || 0,
        totalCost: 0
      };
      setOrderItems(prev => [...prev, newItem]);
    }
    
    setSearchQuery('');
    setShowIngredients(false);
  };

  const updateOrderItem = (ingredientId: string, field: 'quantity' | 'unitCost', value: number) => {
    setOrderItems(prev => prev.map(item =>
      item.ingredientId === ingredientId
        ? {
            ...item,
            [field]: value,
            totalCost: field === 'quantity' ? value * item.unitCost : item.quantity * value
          }
        : item
    ));
  };

  const removeOrderItem = (ingredientId: string) => {
    setOrderItems(prev => prev.filter(item => item.ingredientId !== ingredientId));
  };

  const calculateTotals = () => {
    const subtotal = orderItems.reduce((sum, item) => sum + item.totalCost, 0);
    const tax = subtotal * 0.18;
    const total = subtotal + tax;
    return { subtotal, tax, total };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.supplierName || orderItems.length === 0) {
      return;
    }

    setSubmitting(true);
    
    try {
      const orderData: PurchaseOrderFormData = {
        supplierName: formData.supplierName,
        supplierId: formData.supplierId,
        locationId: formData.locationId,
        items: orderItems,
        expectedDeliveryDate: formData.expectedDeliveryDate,
        notes: formData.notes
      };

      const success = await onSubmit(orderData);
      
      if (success) {
        resetForm();
        onClose();
      }
    } catch (error) {
      console.error('Error creating purchase order:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      supplierName: '',
      supplierId: '',
      locationId: 'loc-1',
      expectedDeliveryDate: '',
      notes: ''
    });
    setOrderItems([]);
    setSearchQuery('');
    setShowIngredients(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN'
    }).format(amount);
  };

  const { subtotal, tax, total } = calculateTotals();

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-[95vw] max-h-[95vh] w-full overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-macartur-primary" />
            Nueva Orden de Compra
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Supplier Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Información del Proveedor</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="supplier">Proveedor *</Label>
                  {suppliers.length > 0 ? (
                    <Select 
                      value={formData.supplierId} 
                      onValueChange={(value) => {
                        const supplier = suppliers.find(s => s.id === value);
                        setFormData(prev => ({
                          ...prev,
                          supplierId: value,
                          supplierName: supplier?.name || ''
                        }));
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar proveedor" />
                      </SelectTrigger>
                      <SelectContent>
                        {suppliers.map(supplier => (
                          <SelectItem key={supplier.id} value={supplier.id}>
                            {supplier.businessName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      id="supplier"
                      placeholder="Nombre del proveedor"
                      value={formData.supplierName}
                      onChange={(e) => setFormData(prev => ({ ...prev, supplierName: e.target.value }))}
                      required
                    />
                  )}
                </div>

                <div>
                  <Label htmlFor="location">Sucursal *</Label>
                  <Select 
                    value={formData.locationId || "loc-1"} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, locationId: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar sucursal" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="loc-1">Sede Principal</SelectItem>
                      <SelectItem value="loc-2">Sucursal Norte</SelectItem>
                      <SelectItem value="loc-3">Sucursal Sur</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="deliveryDate">Fecha Esperada de Entrega</Label>
                  <Input
                    id="deliveryDate"
                    type="date"
                    value={formData.expectedDeliveryDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, expectedDeliveryDate: e.target.value }))}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Order Items */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-macartur-primary" />
                  Productos de la Orden
                </div>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => setShowIngredients(!showIngredients)}
                  className="bg-macartur-primary hover:bg-macartur-primary/90"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Agregar Producto
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Ingredient Search */}
              {showIngredients && (
                <div className="border rounded-lg p-4">
                  <div className="relative mb-3">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Buscar productos..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  
                  {searchQuery && (
                    <div className="max-h-40 overflow-y-auto border rounded-md">
                      {filteredIngredients.map(ingredient => (
                        <div
                          key={ingredient.id}
                          className="p-2 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                          onClick={() => addIngredientToOrder(ingredient)}
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <p className="font-medium">{ingredient.name}</p>
                              <p className="text-sm text-gray-500">
                                {ingredient.category} • {formatCurrency(ingredient.costPerUnit || ingredient.globalCostPerUnit || 0)}/{ingredient.unit}
                              </p>
                            </div>
                            <Plus className="w-4 h-4 text-gray-400" />
                          </div>
                        </div>
                      ))}
                      
                      {filteredIngredients.length === 0 && (
                        <div className="p-4 text-center text-gray-500">
                          No se encontraron productos
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Order Items List */}
              {orderItems.length > 0 && (
                <div className="space-y-2">
                  {orderItems.map((item) => (
                    <div key={item.ingredientId} className="flex items-center gap-3 p-3 border rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium">{item.ingredient.name}</p>
                        <p className="text-sm text-gray-500">{item.ingredient.category}</p>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <div>
                          <Label className="text-xs">Cantidad</Label>
                          <Input
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={item.quantity}
                            onChange={(e) => updateOrderItem(item.ingredientId, 'quantity', parseFloat(e.target.value) || 0.01)}
                            className="w-20 text-center"
                          />
                        </div>
                        
                        <div>
                          <Label className="text-xs">Costo Unit.</Label>
                          <Input
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={item.unitCost}
                            onChange={(e) => updateOrderItem(item.ingredientId, 'unitCost', parseFloat(e.target.value) || 0.01)}
                            className="w-24 text-center"
                          />
                        </div>
                        
                        <div className="text-right min-w-[80px]">
                          <p className="text-xs text-gray-500">Total</p>
                          <p className="font-medium">{formatCurrency(item.totalCost)}</p>
                        </div>
                        
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeOrderItem(item.ingredientId)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  
                  {/* Order Summary */}
                  <div className="border-t pt-3 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Subtotal:</span>
                      <span>{formatCurrency(subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>IGV (18%):</span>
                      <span>{formatCurrency(tax)}</span>
                    </div>
                    <div className="flex justify-between font-bold text-lg border-t pt-2">
                      <span>Total:</span>
                      <span className="text-macartur-primary">{formatCurrency(total)}</span>
                    </div>
                  </div>
                </div>
              )}
              
              {orderItems.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No hay productos en la orden</p>
                  <p className="text-sm">Haz clic en "Agregar Producto" para comenzar</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          <div>
            <Label htmlFor="notes">Notas (Opcional)</Label>
            <Textarea
              id="notes"
              placeholder="Notas adicionales para la orden..."
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              className="mt-1"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={!formData.supplierName || orderItems.length === 0 || submitting}
              className="bg-macartur-primary hover:bg-macartur-primary/90"
            >
              {submitting ? 'Creando...' : 'Crear Orden'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};